<template>
  <!-- Cancellation rule warnings (DEV-626) · ochre advisories: rules only warn, never block. Margin is
       left to the caller via class fallthrough, matching RegionMismatchAdvisory. -->
  <ul
    v-if="groups.length > 0"
    class="list-unstyled d-flex flex-column gap-2 mb-0"
  >
    <li
      v-for="group in groups"
      :key="group.key"
      class="advisory-note rule-warning"
      :class="{ 'rule-warning--compact': compact }"
      data-test="rule-warning"
    >
      <FontAwesomeIcon
        :icon="['far', 'circle-info']"
        class="flex-shrink-0 mt-1"
      />
      <div>
        <div :class="{ 'fw-semibold': !compact }">{{ group.message }}</div>
        <div v-for="detail in group.details" :key="detail" class="small">
          {{ detail }}
        </div>
        <div v-if="namesStatements" class="small">
          {{
            t("cancellation_rules.concerns_statements", {
              statements: describeStatements(group),
            })
          }}
        </div>
      </div>
    </li>
  </ul>
</template>

<script setup lang="ts">
import type { RuleWarningGroup } from "@/dashboard/inventory_management/cancel/cancellationRuleMessages";
import { useI18n } from "vue-i18n";

// Long enough to name the statements of a typical order, short enough to stay one line.
const MAX_NAMED_STATEMENTS = 5;

/**
 * Lists grouped cancellation rule warnings (`groupRuleWarnings`). With `namesStatements` each warning says which
 * statements it concerns; a row that shows its own warnings leaves that out.
 */
withDefaults(
  defineProps<{
    groups: RuleWarningGroup[];
    namesStatements?: boolean;
    compact?: boolean;
  }>(),
  { namesStatements: true, compact: false }
);

const { t } = useI18n();

const describeStatements = (group: RuleWarningGroup): string => {
  const labels = group.statements.map((statement) => statement.label);
  const named = labels.slice(0, MAX_NAMED_STATEMENTS).join(", ");
  const hidden = labels.length - MAX_NAMED_STATEMENTS;
  return hidden > 0
    ? `${named} ${t("cancellation_rules.more_statements", { count: hidden })}`
    : named;
};
</script>

<style scoped lang="scss">
.rule-warning--compact {
  padding: 0.5rem 0.75rem;
  font-size: 0.875em;
}
</style>
